// src/app/redux/sagas/candidatsSaga.js
import { call, put, takeLatest, all } from "redux-saga/effects";
import Swal from "sweetalert2";

import {
    FETCH_CANDIDATS_REQUEST,
    fetchCandidatsSuccess,
    fetchCandidatsFail,

    CREATE_CANDIDAT_REQUEST,
    createCandidatSuccess,
    createCandidatFail,

    UPDATE_CANDIDAT_REQUEST,
    updateCandidatSuccess,
    updateCandidatFail,

    DELETE_CANDIDAT_REQUEST,
    deleteCandidatSuccess,
    deleteCandidatFail,

    setCandidatsLoading,
} from "../actions/candidatsActions";

import { API_BASE_URL, getRequest, postRequest, putRequest, deleteRequest } from "../../../api";

const getUid = () => sessionStorage.getItem("uid");
const getToken = () => sessionStorage.getItem("token");

// ===== Fetch =====
export function* fetchCandidatsSaga() {
    const url = `${API_BASE_URL}/api/candidat-theque`;
    const uid = getUid();
    const token = getToken();

    yield put(setCandidatsLoading(true));
    try {
        const resp = yield call(getRequest, { url, uid, token });
        yield put(fetchCandidatsSuccess(resp.data));
    } catch (e) {
        yield put(fetchCandidatsFail(e.response?.data?.message || e.message));
    } finally {
        yield put(setCandidatsLoading(false));
    }
}

// ===== Create =====
export function* createCandidatSaga(action) {
    const url = `${API_BASE_URL}/api/candidat-theque`;
    const uid = getUid();
    const token = getToken();

    yield put(setCandidatsLoading(true));
    try {
        const resp = yield call(postRequest, {
            url,
            data: action.payload,
            uid,
            token,
            headers: { "Content-Type": "multipart/form-data" },
        });

        yield put(createCandidatSuccess(resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Candidat ajouté",
            timer: 1400,
            showConfirmButton: false,
        });

        yield put({ type: FETCH_CANDIDATS_REQUEST });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur ajout candidat";

        if (msg.startsWith("DUPLICATE_BLOCK|")) {
            const cleanMsg = msg.replace("DUPLICATE_BLOCK|", "");
            yield put(createCandidatFail(cleanMsg));

            yield call([Swal, Swal.fire], {
                icon: "error",
                text: cleanMsg,
                timer: 2600,
                showConfirmButton: false,
                customClass: {
                    container: "swal-above-modal",
                },
                didOpen: () => {
                    const container = document.querySelector(".swal-above-modal");
                    if (container) container.style.zIndex = "99999";
                },
            });
            return;
        }

        if (msg.startsWith("DUPLICATE_WARNING|")) {
            const cleanMsg = msg.replace("DUPLICATE_WARNING|", "");

            const result = yield call([Swal, Swal.fire], {
                icon: "warning",
                text: cleanMsg,
                showCancelButton: true,
                confirmButtonText: "Ignorer et continuer",
                cancelButtonText: "Annuler",
                customClass: {
                    container: "swal-above-modal",
                },
                didOpen: () => {
                    const container = document.querySelector(".swal-above-modal");
                    if (container) container.style.zIndex = "99999";
                },
            });

            if (result.isConfirmed) {
                const originalPayload = action.payload;
                const rawPayload = originalPayload.get("payload");
                const parsedPayload = JSON.parse(rawPayload);

                parsedPayload.ignoreDuplicateWarning = true;

                const retryFormData = new FormData();
                retryFormData.append("payload", JSON.stringify(parsedPayload));

                const cvFile = originalPayload.get("cv");
                const pieceFile = originalPayload.get("pieceDocument");

                if (cvFile) retryFormData.append("cv", cvFile);
                if (pieceFile) retryFormData.append("pieceDocument", pieceFile);

                const retryResp = yield call(postRequest, {
                    url,
                    data: retryFormData,
                    uid,
                    token,
                    headers: { "Content-Type": "multipart/form-data" },
                });

                yield put(createCandidatSuccess(retryResp.data));

                yield call([Swal, Swal.fire], {
                    icon: "success",
                    text: "Candidat ajouté malgré l'avertissement",
                    timer: 1600,
                    showConfirmButton: false,
                });

                yield put({ type: FETCH_CANDIDATS_REQUEST });
            } else {
                yield put(createCandidatFail(cleanMsg));
            }
            return;
        }

        yield put(createCandidatFail(msg));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        yield put(setCandidatsLoading(false));
    }
}
// ===== Update =====
export function* updateCandidatSaga(action) {
    const { id, payload } = action.payload;
    const url = `${API_BASE_URL}/api/candidat-theque/${id}`;
    const uid = getUid();
    const token = getToken();

    yield put(setCandidatsLoading(true));
    try {
        const resp = yield call(putRequest, {
            url,
            data: payload, // FormData
            uid,
            token,
            headers: { "Content-Type": "multipart/form-data" },
        });

        yield put(updateCandidatSuccess(resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Candidat modifié",
            timer: 1400,
            showConfirmButton: false,
        });

        yield put({ type: FETCH_CANDIDATS_REQUEST });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur modification candidat";
        yield put(updateCandidatFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", text: msg, timer: 2200, showConfirmButton: false });
    } finally {
        yield put(setCandidatsLoading(false));
    }
}

// ===== Delete =====
export function* deleteCandidatSaga(action) {
    const { id } = action.payload;
    const url = `${API_BASE_URL}/api/candidat-theque/${id}`;
    const uid = getUid();
    const token = getToken();

    yield put(setCandidatsLoading(true));
    try {
        yield call(deleteRequest, { url, uid, token });
        yield put(deleteCandidatSuccess(id));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Candidat supprimé",
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur suppression candidat";
        yield put(deleteCandidatFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", text: msg, timer: 2200, showConfirmButton: false });
    } finally {
        yield put(setCandidatsLoading(false));
    }
}

// ===== Watcher =====
export function* candidatsWatcher() {
    yield all([
        takeLatest(FETCH_CANDIDATS_REQUEST, fetchCandidatsSaga),
        takeLatest(CREATE_CANDIDAT_REQUEST, createCandidatSaga),
        takeLatest(UPDATE_CANDIDAT_REQUEST, updateCandidatSaga),
        takeLatest(DELETE_CANDIDAT_REQUEST, deleteCandidatSaga),
    ]);
}
